import { Elements, ExpressCheckoutElement, useElements } from '@stripe/react-stripe-js';
import { useContext, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import type {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { APPLE_PAY_PAYMENT_CONFIG } from '@/constants/stripe';
import { PaymentMethodEnum, Templates } from '@/types/common';
import { useTranslate } from '@/hooks/useTranslate';
import { mixpanelService } from '@/services/mixpanelService';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { LoaderPayButton } from '../LoaderPayButton/LoaderPayButton';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';
import { trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';

interface ApplePayButtonUIProps {
  isLoading: boolean;
  isAvailable: boolean;
  onReady: () => void;
  onConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => void;
  onLoadError: () => void;
}

const ApplePayButtonUI = ({ isLoading, isAvailable, onReady, onConfirm, onLoadError }: ApplePayButtonUIProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { setError },
    payment: { isFormValid },
    customer,
  } = context;
  const { t } = useTranslate();
  if (!isAvailable) {
    return null;
  }

  const handleClick = (event: StripeExpressCheckoutElementClickEvent) => {
    if (isFormValid) {
      trackAndNotifyPaymentClick(context, PaymentMethodEnum.APPLE_PAY);
      event.resolve({ emailRequired: true });
    } else {
      const errorMessageKey = getZipCodeErrorMessageKey(customer);
      setError(t(errorMessageKey));
      event.reject();
    }
  };
  return (
    <div className="flex flex-col w-full min-h-11">
      <div className={`transition-opacity duration-500 ${!isLoading && 'hidden'}`}>
        <LoaderPayButton type={PaymentMethodEnum.APPLE_PAY} />
      </div>
      <div className={`transition-opacity duration-500 ${isLoading && 'hidden'}`}>
        <ExpressCheckoutElement
          onClick={handleClick}
          onConfirm={onConfirm}
          onReady={onReady}
          onLoadError={onLoadError}
          options={APPLE_PAY_PAYMENT_CONFIG}
        />
      </div>
    </div>
  );
};

const ApplePayButton = () => {
  const {
    actions: { runPayment, setError },
    template,
  } = useContext(CheckoutContext);
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(true);
  const [isAvailable, setIsAvailable] = useState(true);
  const isOnePayment = template.type === Templates.OnePayment;
  const { t } = useTranslate();

  const handleReady = () => {
    setTimeout(() => setIsLoading(false), 500);
  };

  const handleLoadError = (error?: unknown) => {
    console.error('Apple Pay load error:', error);
    if (error && isOnePayment) {
      setError(t('paymentMethodNotAvailable'));
      return;
    }
    setIsAvailable(false);
    setIsLoading(false);
  };

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    try {
      await runPayment({ event, elements: elements! }, PaymentMethodEnum.APPLE_PAY);
    } catch (error: unknown) {
      console.error(t('somethingWentWrong'), { cause: error });
    }
  };

  return (
    <ApplePayButtonUI
      isLoading={isLoading}
      isAvailable={isAvailable}
      onReady={handleReady}
      onConfirm={handleConfirm}
      onLoadError={handleLoadError}
    />
  );
};

export const ApplePayStripeForm = () => {
  const context = useContext(CheckoutContext);
  const {
    pricing,
    actions: { setError },
    template,
  } = context;
  const isOnePayment = template.type === Templates.OnePayment;
  const { t } = useTranslate();
  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({
    paymentMethod: PaymentMethodEnum.APPLE_PAY,
    isExpress: true,
  });

  if (!stripe || isStripeLoading) {
    return null;
  }

  try {
    return (
      <Elements
        stripe={stripe}
        options={{
          mode: 'payment',
          amount: Math.round(pricing.priceBreakdown.priceTotal),
          currency: pricing.currencyCode.toLowerCase(),
          loader: 'never',
          paymentMethodCreation: 'manual',
        }}
      >
        <ApplePayButton />
      </Elements>
    );
  } catch (error) {
    console.error('Apple Pay Stripe Elements initialization error:', error);
    if (isOnePayment) {
      setError(t('paymentMethodNotAvailable'));
      mixpanelService.track(
        MixpanelEvents.PAYMENT_NOT_SUPPORTED,
        getMixpanelProperties(context) as Record<string, unknown>
      );
    }
    return null;
  }
};
